"use client";

// contexts/InquiryCartContext.tsx
// سبد استعلام قیمت — در localStorage ذخیره می‌شود تا مهمان هم بتواند محصول اضافه کند.

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import type {
    CustomItemInput,
    InquiryItem,
    SiteProductInput,
} from "@/types/inquiry";

const STORAGE_KEY = "ittelecom_inquiry_cart";
const MAX_QTY = 100000;

interface InquiryCartContextType {
    items: InquiryItem[];
    hydrated: boolean;
    totalLines: number;
    totalUnits: number;
    addSiteProduct: (product: SiteProductInput, qty?: number) => "added" | "updated";
    addCustomItem: (data: CustomItemInput) => void;
    setQty: (key: string, qty: number) => void;
    removeItem: (key: string) => void;
    clear: () => void;
}

const InquiryCartContext = createContext<InquiryCartContextType | undefined>(undefined);

const clampQty = (n: number) => Math.min(MAX_QTY, Math.max(1, Math.floor(Number(n)) || 1));

function isValidItem(x: unknown): x is InquiryItem {
    if (!x || typeof x !== "object") return false;
    const i = x as Partial<InquiryItem>;
    return (
        typeof i.key === "string" &&
        (i.type === "site" || i.type === "custom") &&
        typeof i.name === "string" &&
        typeof i.qty === "number"
    );
}

function loadFromStorage(): InquiryItem[] {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        if (!Array.isArray(parsed)) return [];
        return parsed.filter(isValidItem).map((i) => ({ ...i, qty: clampQty(i.qty) }));
    } catch {
        return [];
    }
}

export function InquiryCartProvider({ children }: { children: React.ReactNode }) {
    const [items, setItems] = useState<InquiryItem[]>([]);
    const [hydrated, setHydrated] = useState(false);

    // بارگذاری اولیه (فقط سمت کلاینت)
    useEffect(() => {
        setItems(loadFromStorage());
        setHydrated(true);
    }, []);

    // ذخیره‌سازی بعد از هر تغییر
    useEffect(() => {
        if (!hydrated) return;
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
        } catch {
            /* حافظه پر یا غیرفعال است؛ سبد فقط در حافظه می‌ماند */
        }
    }, [items, hydrated]);

    // همگام‌سازی بین تب‌ها
    useEffect(() => {
        const onStorage = (e: StorageEvent) => {
            if (e.key === STORAGE_KEY) setItems(loadFromStorage());
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, []);

    const addSiteProduct = useCallback(
        (product: SiteProductInput, qty = 1): "added" | "updated" => {
            const key = `site-${product.id}`;
            let result: "added" | "updated" = "added";
            setItems((prev) => {
                const existing = prev.find((i) => i.key === key);
                if (existing) {
                    result = "updated";
                    return prev.map((i) =>
                        i.key === key
                            ? { ...i, qty: clampQty(i.qty + qty), price: product.price ?? i.price }
                            : i
                    );
                }
                return [
                    ...prev,
                    {
                        key,
                        type: "site",
                        productId: product.id,
                        name: product.name,
                        part: product.sku,
                        slug: product.slug,
                        image: product.image,
                        price: product.price,
                        qty: clampQty(qty),
                    },
                ];
            });
            return result;
        },
        []
    );

    const addCustomItem = useCallback((data: CustomItemInput) => {
        const key = `custom-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
        setItems((prev) => [
            ...prev,
            {
                key,
                type: "custom",
                name: data.name.trim(),
                part: (data.part || "").trim(),
                brand: (data.brand || "").trim(),
                description: (data.description || "").trim(),
                link: (data.link || "").trim(),
                qty: clampQty(data.qty),
            },
        ]);
    }, []);

    const setQty = useCallback((key: string, qty: number) => {
        setItems((prev) => prev.map((i) => (i.key === key ? { ...i, qty: clampQty(qty) } : i)));
    }, []);

    const removeItem = useCallback((key: string) => {
        setItems((prev) => prev.filter((i) => i.key !== key));
    }, []);

    const clear = useCallback(() => setItems([]), []);

    const value = useMemo<InquiryCartContextType>(
        () => ({
            items,
            hydrated,
            totalLines: items.length,
            totalUnits: items.reduce((s, i) => s + i.qty, 0),
            addSiteProduct,
            addCustomItem,
            setQty,
            removeItem,
            clear,
        }),
        [items, hydrated, addSiteProduct, addCustomItem, setQty, removeItem, clear]
    );

    return <InquiryCartContext.Provider value={value}>{children}</InquiryCartContext.Provider>;
}

export function useInquiryCart() {
    const ctx = useContext(InquiryCartContext);
    if (!ctx) throw new Error("useInquiryCart must be used within an InquiryCartProvider");
    return ctx;
}
